'use es6';

import getLang from 'I18n/utils/getLang';
import CanonicalLink from 'marketplace-ui-common/components/elements/CanonicalLink';
import HrefLangLink from 'marketplace-ui-common/components/elements/HrefLangLink';
import MetaDescription from 'marketplace-ui-common/components/elements/MetaDescription';
import MetaOpenGraph from 'marketplace-ui-common/components/elements/MetaOpenGraph';
import MetaRobots from 'marketplace-ui-common/components/elements/MetaRobots';
import MetaTwitter from 'marketplace-ui-common/components/elements/MetaTwitter';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
import { PUBLIC_LANGUAGES } from 'marketplace-ui-common/constants/Languages';
import * as RequestState from 'marketplace-ui-common/requestStates/RequestState';
import { isFulfilled, isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import HSPageViewTracker from 'marketplace-ui-common/tracking/components/HSPageViewTracker';
import PropTypes from 'prop-types';
import { Fragment, useMemo } from 'react';
import { useSelector } from 'react-redux';
import * as FilterKey from '../constants/search/FilterKey';
import { useSocialScreenshotUrl } from '../hooks/useSocialScreenshot';
import { PublicAppComponent, getCanonicalAppContext } from '../selectors/app';
import { getServices } from '../slices/serviceCatalogSlice';
import { getStorefrontMetaDescription } from '../utils/i18n';
import { getFullLocationPathname, getFullLocationUrl, getPagePathname } from '../utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getCanonicalLocation = (pathname, pageNumber) => ({
  pathname: getPagePathname(pathname, pageNumber),
  search: ''
});
const StorefrontPageMeta = ({
  accreditationsRequestState,
  certificationsRequestState,
  servicesRequestState,
  filters,
  location,
  pageCount,
  pageNumber,
  pageTitle,
  profilesRequestState,
  profilesTotal
}) => {
  const {
    pathname,
    search
  } = location;
  const lang = getLang();
  const profilesRequestSucceeded = isSucceeded(profilesRequestState);
  const profilesRequestFulfilled = isFulfilled(profilesRequestState);
  const accreditationsFuilfilled = isFulfilled(accreditationsRequestState);
  const certificationsFulfilled = isFulfilled(certificationsRequestState);
  const servicesFulfilled = isFulfilled(servicesRequestState);
  const canonicalAppContext = useSelector(getCanonicalAppContext);
  const catalogService = useSelector(getServices);
  const socialImageUrl = useSocialScreenshotUrl();
  const canonicalLocation = useMemo(() => {
    return getCanonicalLocation(pathname, pageNumber);
  }, [pageNumber, pathname]);
  const [hsPageViewPathname, hsPageViewKey] = useMemo(() => {
    const {
      pathname: canonicalPathname,
      search: canonicalSearch
    } = canonicalLocation;
    const fullPathname = getFullLocationPathname(canonicalPathname, canonicalAppContext, lang);
    return [`${fullPathname}${canonicalSearch}`, `${fullPathname}${search}`];
  }, [canonicalAppContext, canonicalLocation, lang, search]);
  const renderedCanonicalLinks = useMemo(() => {
    const canonicalUrl = getFullLocationUrl(canonicalLocation, canonicalAppContext, lang);
    let prevCanonicalUrl;
    let nextCanonicalUrl;
    if (pageNumber > 1) {
      prevCanonicalUrl = getFullLocationUrl(getCanonicalLocation(pathname, pageNumber - 1), canonicalAppContext, lang);
    }
    if (pageNumber < pageCount) {
      nextCanonicalUrl = getFullLocationUrl(getCanonicalLocation(pathname, pageNumber + 1), canonicalAppContext, lang);
    }
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(CanonicalLink, {
        href: canonicalUrl,
        rel: "canonical"
      }), prevCanonicalUrl && /*#__PURE__*/_jsx(CanonicalLink, {
        href: prevCanonicalUrl,
        rel: "prev"
      }), nextCanonicalUrl && /*#__PURE__*/_jsx(CanonicalLink, {
        href: nextCanonicalUrl,
        rel: "next"
      })]
    });
  }, [canonicalAppContext, canonicalLocation, lang, pageCount, pageNumber, pathname]);
  const renderedHrefLangLinks = useMemo(() => {
    if (pathname !== '/' || pageNumber > 1) return null;
    return PUBLIC_LANGUAGES.map(linkLang => /*#__PURE__*/_jsx(HrefLangLink, {
      href: getFullLocationUrl(canonicalLocation, canonicalAppContext, linkLang),
      lang: linkLang,
      "x-default": linkLang === 'en'
    }, linkLang));
  }, [pathname, pageNumber, canonicalLocation, canonicalAppContext]);
  const metaDescription = useMemo(() => {
    return getStorefrontMetaDescription(filters, catalogService);
  }, [catalogService, filters]);
  const noFollowIndex = useMemo(() => {
    if (!profilesRequestSucceeded) {
      return false;
    }
    if (filters[FilterKey.SEARCH]) {
      return true;
    }
    if (profilesTotal < 3) {
      return true;
    }
    return false;
  }, [filters, profilesRequestSucceeded, profilesTotal]);
  const socialUrl = useMemo(() => {
    return getFullLocationUrl(location, canonicalAppContext, lang);
  }, [canonicalAppContext, lang, location]);
  const renderedPrerenderReady = useMemo(() => {
    const ready = profilesRequestFulfilled && accreditationsFuilfilled && certificationsFulfilled && servicesFulfilled;
    let statusCode;
    if (profilesRequestFulfilled && !profilesTotal) {
      statusCode = 404;
    }
    return /*#__PURE__*/_jsx(PrerenderReady, {
      ready: ready,
      statusCode: statusCode
    });
  }, [accreditationsFuilfilled, certificationsFulfilled, profilesRequestFulfilled, servicesFulfilled, profilesTotal]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(PublicAppComponent, {
      children: /*#__PURE__*/_jsxs(DocumentHead, {
        children: [/*#__PURE__*/_jsx(MetaDescription, {
          content: metaDescription
        }), /*#__PURE__*/_jsx(MetaRobots, {
          noFollow: noFollowIndex,
          noIndex: noFollowIndex,
          directive: noFollowIndex ? ['nofollow', 'noindex'] : undefined
        }), renderedCanonicalLinks, renderedHrefLangLinks, /*#__PURE__*/_jsx(MetaTwitter, {
          name: "card"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "site"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "type"
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "title",
          content: pageTitle
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "url",
          content: socialUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "description",
          content: metaDescription
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "image",
          content: socialImageUrl
        }), renderedPrerenderReady]
      })
    }), /*#__PURE__*/_jsx(HSPageViewTracker, {
      pathname: hsPageViewPathname
    }, hsPageViewKey)]
  });
};
StorefrontPageMeta.propTypes = {
  accreditationsRequestState: PropTypes.oneOf(Object.values(RequestState)).isRequired,
  certificationsRequestState: PropTypes.oneOf(Object.values(RequestState)).isRequired,
  servicesRequestState: PropTypes.oneOf(Object.values(RequestState)).isRequired,
  filters: PropTypes.object.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired
  }).isRequired,
  pageCount: PropTypes.number.isRequired,
  pageNumber: PropTypes.number.isRequired,
  pageTitle: PropTypes.string.isRequired,
  profilesRequestState: PropTypes.oneOf(Object.values(RequestState)).isRequired,
  profilesTotal: PropTypes.number.isRequired
};
export default StorefrontPageMeta;